import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  BuildingOffice2Icon,
  CalendarIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  MapPinIcon,
  Squares2X2Icon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { usePendencias } from '../hooks/useConferencias';
import { CATEGORIAS_PENDENCIA, type CategoriaPendencia, type ConferenciaListItem } from '../types/conferencia';
import { formatDateTime } from '../utils/format';
import { colunaDaPendencia } from '../utils/statusConferencia';
import { BarraFiltros, FiltroPeriodo } from '../components/ui/Filtros';
import QuadroKanban, { type ColunaKanban } from '../components/ui/QuadroKanban';

// yyyy-MM-dd em horário local (molde CotacoesPage.tsx:65-74).
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const diasAtras = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return ymd(d);
};

/** Chave → rótulo PT das categorias (CATEGORIAS_PENDENCIA é a fonte-da-verdade). */
const LABEL_CATEGORIA: Record<string, string> = Object.fromEntries(
  CATEGORIAS_PENDENCIA.map((c) => [c.chave, c.label])
);

/** `pendenciaCategorias` é "avaria,falta_volumes" (ou null); aqui explode pra lista. */
const categoriasDe = (c: Pick<ConferenciaListItem, 'pendenciaCategorias'>): string[] =>
  (c.pendenciaCategorias ?? '').split(',').filter(Boolean);

type Aba = 'abertas' | 'resolvidas';

/** Segmented control Íris (mesmo idioma do toggle de câmera em ConferenciaNovaPage). */
const segButton = (ativo: boolean) =>
  `h-11 rounded-iris-sm px-3 text-sm font-medium transition-colors md:h-8 ${
    ativo ? 'bg-accent text-white' : 'text-tx-2 hover:bg-surface-2'
  }`;

/** Chip de filtro por categoria — tom warning quando ativo (pendência = warning na Íris). */
const chipCategoria = (ativo: boolean) =>
  `h-11 flex-shrink-0 rounded-full border px-3 text-sm font-medium transition-colors md:h-8 ${
    ativo
      ? 'border-warning-border bg-warning-soft text-warning'
      : 'border-line-strong bg-surface text-tx-2 hover:bg-surface-2'
  }`;

/** Colunas do Kanban de Pendências: as 5 categorias + "Múltiplas" (nota com 2+ categorias). */
const COLUNAS_KANBAN_PENDENCIA: ColunaKanban[] = [
  ...CATEGORIAS_PENDENCIA.map((c) => ({ chave: c.chave as string, titulo: c.label })),
  { chave: 'multiplas', titulo: 'Múltiplas' },
];

/** Card compacto de uma pendência no board — clica pra abrir o mesmo dialog de detalhe da lista. */
const CartaoKanban: React.FC<{
  c: ConferenciaListItem;
  aba: Aba;
  onAbrir: (c: ConferenciaListItem) => void;
}> = ({ c, aba, onAbrir }) => {
  const categorias = categoriasDe(c);
  return (
    <button
      type="button"
      onClick={() => onAbrir(c)}
      className="w-full rounded-iris border border-line bg-surface p-2.5 text-left transition-colors hover:border-line-strong hover:bg-surface-2 focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-semibold text-tx">
          NF-e {c.numeroNfe ?? '—'}{c.serieNfe ? `/${c.serieNfe}` : ''}
        </span>
        <span className="flex-shrink-0 text-[10px] tabular-nums text-tx-3">
          {formatDateTime(aba === 'abertas' ? c.concluidoEm : c.pendenciaResolvidaEm)}
        </span>
      </div>
      <p className="mt-0.5 truncate text-[13px] text-tx-2" title={c.fornecedorNome ?? undefined}>
        {c.fornecedorNome || 'Fornecedor não informado'}
      </p>
      {categorias.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {categorias.map((chave) => (
            <span
              key={chave}
              className="rounded-full border border-warning-border bg-warning-soft px-1.5 py-0.5 text-[10px] font-medium text-warning"
            >
              {LABEL_CATEGORIA[chave] ?? chave}
            </span>
          ))}
        </div>
      )}
      {c.setorRecebimento && (
        <div className="mt-1.5 flex items-center gap-1 text-[11px] text-tx-3">
          <MapPinIcon className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{c.setorRecebimento}</span>
        </div>
      )}
    </button>
  );
};

/**
 * Tela de acompanhamento das pendências de recebimento declaradas na conclusão da
 * conferência (categorias em CATEGORIAS_PENDENCIA). Consome `pendencia=com_pendencia`
 * (default) ou `pendencia=resolvida` — o backend só aceita um dos dois nesse filtro
 * (não existe "as duas juntas"), então o toggle Não-resolvidas/Resolvidas troca o
 * parâmetro em vez de filtrar client-side. Filtro por categoria é client-side (chips)
 * sobre o subconjunto já pequeno que a chamada trouxe.
 */
const ConferenciaPendenciasPage: React.FC = () => {
  const navigate = useNavigate();

  const [aba, setAba] = useState<Aba>('abertas');
  const [vista, setVista] = useState<'lista' | 'quadro'>('lista');
  const [catFiltro, setCatFiltro] = useState<CategoriaPendencia | null>(null);
  // Default: dos últimos 60 dias PRA CÁ — só o limite INFERIOR (molde CotacoesPage, lazy
  // initializer no browser). SEM teto superior de propósito: o backend filtra `CriadoEm <= fim`
  // e `fim` é uma DATA (00:00 UTC), então um `periodoFim = hoje` excluiria tudo que foi criado
  // hoje (ainda pior com o offset de fuso — GMT-3 empurra o CriadoEm pro UTC de amanhã). Sem
  // teto, hoje entra. O "✕" do FiltroPeriodo limpa o início pra ver todo o histórico.
  const [periodoIni, setPeriodoIni] = useState(() => diasAtras(60));
  const [periodoFim, setPeriodoFim] = useState('');
  const [setorInput, setSetorInput] = useState('');
  const [setorTerm, setSetorTerm] = useState('');

  // Debounce do setor (texto livre, filtro server-side por igualdade exata) — mesmo
  // padrão de busca da ConferenciaListPage, só que aqui vai pro backend, não local.
  useEffect(() => {
    const t = setTimeout(() => setSetorTerm(setorInput.trim()), 400);
    return () => clearTimeout(t);
  }, [setorInput]);

  const {
    data: conferencias,
    isLoading,
    error,
    refetch,
    isFetching,
  } = usePendencias({
    pendencia: aba === 'abertas' ? 'com_pendencia' : 'resolvida',
    periodoIni: periodoIni || undefined,
    periodoFim: periodoFim || undefined,
    setor: setorTerm || undefined,
  });

  const lista = useMemo(() => {
    const base = conferencias ?? [];
    if (!catFiltro) return base;
    return base.filter((c) => categoriasDe(c).includes(catFiltro));
  }, [conferencias, catFiltro]);

  return (
    <div className="mx-auto max-w-[1280px] space-y-4">
      <header>
        <button
          type="button"
          onClick={() => navigate('/conferencia')}
          className="inline-flex items-center gap-1 text-sm font-medium text-tx-3 hover:text-tx-2"
        >
          <ArrowLeftIcon className="h-4 w-4" /> Conferências
        </button>
        <div className="mt-1 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-xl font-semibold tracking-[-0.01em] text-tx">
              <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-warning" />
              Pendências de recebimento
            </h1>
            <p className="mt-0.5 text-sm text-tx-3">
              {aba === 'abertas'
                ? 'Notas concluídas com pendência declarada (avaria, excesso, falta de volumes…) aguardando tratamento.'
                : 'Notas cuja pendência declarada já foi tratada.'}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-iris px-2.5 text-sm font-medium text-tx-2 transition-colors hover:bg-surface-2 disabled:opacity-50"
            title="Atualizar lista"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Toggle: não-resolvidas (default) x resolvidas — troca o parâmetro `pendencia`,
            o backend não tem um valor "as duas juntas". */}
        <div className="inline-flex items-center gap-1 rounded-iris border border-line bg-surface p-1">
          <button type="button" onClick={() => setAba('abertas')} className={segButton(aba === 'abertas')}>
            Não resolvidas
          </button>
          <button type="button" onClick={() => setAba('resolvidas')} className={segButton(aba === 'resolvidas')}>
            Resolvidas
          </button>
        </div>

        {/* Visualização: Lista (tabela/cards) x Quadro (Kanban por categoria). */}
        <div className="inline-flex items-center gap-1 rounded-iris border border-line bg-surface p-1">
          <button
            type="button"
            onClick={() => setVista('lista')}
            className={`${segButton(vista === 'lista')} inline-flex items-center gap-1.5`}
            title="Lista"
          >
            <ListBulletIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Lista</span>
          </button>
          <button
            type="button"
            onClick={() => setVista('quadro')}
            className={`${segButton(vista === 'quadro')} inline-flex items-center gap-1.5`}
            title="Quadro"
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden sm:inline">Quadro</span>
          </button>
        </div>
      </div>

      {/* Filtros: período (server-side, CriadoEm) + setor (server-side, igualdade exata) */}
      <BarraFiltros>
        <FiltroPeriodo
          inicio={periodoIni}
          fim={periodoFim}
          onChange={(i, f) => {
            setPeriodoIni(i);
            setPeriodoFim(f);
          }}
        />
        <div className="relative w-full sm:w-56">
          <MapPinIcon className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-tx-3" />
          <input
            type="text"
            value={setorInput}
            onChange={(e) => setSetorInput(e.target.value)}
            placeholder="Setor de recebimento…"
            className="h-11 w-full rounded-iris-sm border border-line-strong bg-surface-2 pl-8 pr-2 text-sm text-tx placeholder:text-tx-3 outline-none focus:border-accent focus:bg-surface focus:ring-[3px] focus:ring-accent-soft md:h-9"
          />
        </div>
      </BarraFiltros>

      {/* Chips de categoria — só na Lista; no Quadro as colunas JÁ são a visão por categoria. */}
      {vista === 'lista' && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setCatFiltro(null)} className={chipCategoria(catFiltro === null)}>
            Todas categorias
          </button>
          {CATEGORIAS_PENDENCIA.map((c) => (
            <button
              key={c.chave}
              type="button"
              onClick={() => setCatFiltro((prev) => (prev === c.chave ? null : c.chave))}
              className={chipCategoria(catFiltro === c.chave)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-iris-lg border border-danger-border bg-danger-soft p-4">
          <h3 className="text-sm font-semibold text-danger">Erro ao carregar pendências</h3>
          <p className="mt-1 text-sm text-tx-2">Não foi possível carregar a lista. Tente atualizar.</p>
        </div>
      )}

      {!error && vista === 'lista' && (
        <ListaPendencias
          conferencias={lista}
          isLoading={isLoading}
          aba={aba}
          onAbrir={(c) => navigate(`/conferencia/pendencias/${c.id}`)}
        />
      )}

      {!error && vista === 'quadro' && (
        isLoading ? (
          <div className="flex items-center justify-center gap-2 rounded-iris-lg border border-line bg-surface py-12 text-sm text-tx-3">
            <ArrowPathIcon className="h-5 w-5 animate-spin" /> Carregando…
          </div>
        ) : (conferencias ?? []).length === 0 ? (
          <div className="rounded-iris-lg border border-line bg-surface-2 px-6 py-12 text-center">
            <CheckCircleIcon className="mx-auto h-10 w-10 text-ok" />
            <h3 className="mt-3 text-sm font-semibold text-tx">
              {aba === 'abertas' ? 'Nenhuma pendência a tratar' : 'Nenhuma pendência resolvida no período'}
            </h3>
          </div>
        ) : (
          <QuadroKanban
            colunas={COLUNAS_KANBAN_PENDENCIA}
            itens={conferencias ?? []}
            colunaDoItem={(c) => colunaDaPendencia(c.pendenciaCategorias)}
            keyDoItem={(c) => c.id}
            vazioLabel="pendência"
            renderCard={(c) => (
              <CartaoKanban c={c} aba={aba} onAbrir={() => navigate(`/conferencia/pendencias/${c.id}`)} />
            )}
          />
        )
      )}

      {/* Rota filha `:id` — dialog de detalhe (Task 9) monta sobreposto, esta página
          continua embaixo com filtros/lista preservados. */}
      <Outlet />
    </div>
  );
};

interface ListaPendenciasProps {
  conferencias: ConferenciaListItem[];
  isLoading: boolean;
  aba: Aba;
  onAbrir: (c: ConferenciaListItem) => void;
}

/** Tabela densa `md+` / cards `<md` — mesma fronteira/skeleton da ConferenciaListPage. */
const ListaPendencias: React.FC<ListaPendenciasProps> = ({
  conferencias, isLoading, aba, onAbrir,
}) => {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
        <div className="divide-y divide-line">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <div className="h-3 w-10 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="h-3 flex-1 animate-pulse rounded-iris-sm bg-surface-2" />
              <div className="hidden h-3 w-24 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
              <div className="hidden h-3 w-20 animate-pulse rounded-iris-sm bg-surface-2 md:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (conferencias.length === 0) {
    return (
      <div className="rounded-iris-lg border border-line bg-surface-2 px-6 py-12 text-center">
        <CheckCircleIcon className="mx-auto h-10 w-10 text-ok" />
        <h3 className="mt-3 text-sm font-semibold text-tx">
          {aba === 'abertas' ? 'Nenhuma pendência a tratar' : 'Nenhuma pendência resolvida no período'}
        </h3>
        <p className="mt-1 text-sm text-tx-3">
          {aba === 'abertas'
            ? 'Todas as notas com pendência declarada já foram resolvidas.'
            : 'Ajuste os filtros ou troque para "Não resolvidas".'}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-iris-lg border border-line bg-surface">
      {/* Header da tabela densa — só de md pra cima. */}
      <div className="hidden border-b border-line bg-surface-2 px-4 py-2 md:block">
        <div className="grid grid-cols-12 gap-2 text-[11px] font-medium uppercase tracking-wide text-tx-3">
          <div className="col-span-1">NF-e</div>
          <div className="col-span-2">Fornecedor</div>
          <div className="col-span-3">Categorias</div>
          <div className="col-span-2">Setor</div>
          <div className="col-span-2">Data</div>
          <div className="col-span-1">Usuário</div>
          <div className="col-span-1 text-right">Ação</div>
        </div>
      </div>

      <div className="divide-y divide-line">
        {conferencias.map((c) => {
          const categorias = categoriasDe(c);
          return (
            <div
              key={c.id}
              onClick={() => onAbrir(c)}
              className="cursor-pointer transition-colors hover:bg-surface-2"
            >
              {/* ── Mobile / tablet estreito (< md): card ── */}
              <div className="p-3 md:hidden">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-sm font-semibold text-tx">
                      NF-e {c.numeroNfe ?? '—'}{c.serieNfe ? `/${c.serieNfe}` : ''}
                    </span>
                  </div>
                  <div className="text-right text-[11px] tabular-nums text-tx-3">
                    {aba === 'abertas' ? formatDateTime(c.concluidoEm) : formatDateTime(c.pendenciaResolvidaEm)}
                  </div>
                </div>

                <dl className="mt-2 space-y-1.5 text-[13px]">
                  <div className="flex items-center gap-2 text-tx">
                    <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0 text-tx-3" />
                    <span className="truncate font-medium">{c.fornecedorNome || 'Fornecedor não informado'}</span>
                  </div>
                  {c.setorRecebimento && (
                    <div className="flex items-center gap-2 text-tx-3">
                      <MapPinIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{c.setorRecebimento}</span>
                    </div>
                  )}
                  {c.usuario && (
                    <div className="flex items-center gap-2 text-tx-3">
                      <UserIcon className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{c.usuario}</span>
                    </div>
                  )}
                </dl>

                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {categorias.map((chave) => (
                    <span
                      key={chave}
                      className="rounded-full border border-warning-border bg-warning-soft px-2 py-0.5 text-[11px] font-medium text-warning"
                    >
                      {LABEL_CATEGORIA[chave] ?? chave}
                    </span>
                  ))}
                </div>

                {/* "Abertas" não tem ação rápida aqui — toque no card abre o dialog de
                    detalhe (rota filha), onde a resolução realmente vive. */}
                {aba === 'resolvidas' && (
                  <div className="mt-3 border-t border-line pt-3">
                    <p className="flex items-center gap-1.5 text-[11px] font-medium text-ok">
                      <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
                      Pendência resolvida
                    </p>
                  </div>
                )}
              </div>

              {/* ── Desktop (md+): linha densa ── */}
              <div className="hidden grid-cols-12 items-center gap-2 px-4 py-2.5 text-[13px] md:grid">
                <div className="col-span-1 min-w-0">
                  <div className="font-semibold text-tx">
                    {c.numeroNfe ?? '—'}{c.serieNfe ? `/${c.serieNfe}` : ''}
                  </div>
                </div>

                <div className="col-span-2 min-w-0">
                  <div className="truncate font-medium text-tx" title={c.fornecedorNome ?? undefined}>
                    {c.fornecedorNome || 'Não informado'}
                  </div>
                </div>

                <div className="col-span-3 min-w-0 flex flex-wrap gap-1">
                  {categorias.length === 0 ? (
                    <span className="text-tx-3">—</span>
                  ) : (
                    categorias.map((chave) => (
                      <span
                        key={chave}
                        className="rounded-full border border-warning-border bg-warning-soft px-2 py-0.5 text-[11px] font-medium text-warning"
                      >
                        {LABEL_CATEGORIA[chave] ?? chave}
                      </span>
                    ))
                  )}
                </div>

                <div className="col-span-2 min-w-0 truncate text-tx-2">{c.setorRecebimento || '—'}</div>

                <div className="col-span-2 min-w-0">
                  <div className="flex items-center gap-1 tabular-nums text-tx-2">
                    <CalendarIcon className="h-3 w-3 flex-shrink-0 text-tx-3" />
                    {aba === 'abertas' ? formatDateTime(c.concluidoEm) : formatDateTime(c.pendenciaResolvidaEm)}
                  </div>
                </div>

                <div className="col-span-1 min-w-0 truncate text-tx-2" title={c.usuario ?? undefined}>
                  {c.usuario || '—'}
                </div>

                <div className="col-span-1 flex justify-end">
                  {aba === 'resolvidas' && (
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-tx-3" title="Resolvida" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ConferenciaPendenciasPage;
